import type { UICoalescence, UIInventoryItem, UIItemComparisons, UIItemContainerKind } from '@sorrem-client/types';
import classnames from 'classnames';
import { Fragment, useCallback, useRef, type ReactNode } from 'react';
import styles from './ItemGrid.module.scss';
import { CarriedSlotContent } from '../CarriedSlotContent';
import { useItemTransferContext } from '../ItemTransfer';
import { ItemSlot } from '../ItemSlot';

/** Character Home and storage grids never show coalescing crystals. */
const NO_COALESCENCES: readonly UICoalescence[] = [];

export interface ItemGridRearrangeEvent {
    itemId: string;
    fromIndex: number | null;
    toIndex: number;
    displacedItemId: string | null;
}

interface ItemGridProps {
    totalSlots: number;
    items: Array<UIInventoryItem | undefined>;
    container?: UIItemContainerKind;
    size?: 'small' | 'large';
    selectedSlotIndex?: number | null;
    selectedItemId?: string | null;
    onSelectItem?: (item: UIInventoryItem, slotIndex: number, triggerElement: HTMLElement) => void;
    onRearrangeItems?: (event: ItemGridRearrangeEvent, triggerElement: HTMLElement) => void;
    onDeselectSelectedItem?: (triggerElement: HTMLElement) => void;
    onClick?: (item: UIInventoryItem | undefined, slotIndex: number, triggerElement: HTMLElement) => void;
    className?: string;
    ariaLabel?: string;
    /** A label under each slot (e.g. its hotkey); the rows make room for it. None when unset. */
    renderSlotLabel?: (slotIndex: number) => ReactNode;
    /** How items compare with what the character wears (#293, #341), marked with chevrons. */
    itemComparisons?: UIItemComparisons;
}

export function ItemGrid({
    totalSlots,
    items,
    container = 'inventory',
    size = 'small',
    selectedSlotIndex = null,
    selectedItemId = null,
    onSelectItem,
    onRearrangeItems,
    onDeselectSelectedItem,
    onClick,
    className,
    ariaLabel = 'Inventory panel',
    renderSlotLabel,
    itemComparisons,
}: ItemGridProps): JSX.Element {
    const {
        selectedItemId: selectedTransferItemId,
        toggleSelectedItem,
        selectItem,
        clearSelectedItem,
        openItemMenu,
    } = useItemTransferContext();
    const slotElementMapRef = useRef<Record<number, HTMLElement | null>>({});
    const resolvedSelectedItemId = selectedItemId ?? selectedTransferItemId;
    const selectedItemIndex = resolvedSelectedItemId === null
        ? null
        : items.findIndex((entry) => entry?.id === resolvedSelectedItemId);
    const resolvedSelectedSlotIndex = selectedSlotIndex ?? (selectedItemIndex === null || selectedItemIndex === -1
        ? null
        : selectedItemIndex);

    const assignSlotElement = useCallback((slotIndex: number, element: HTMLDivElement | null): void => {
        slotElementMapRef.current[slotIndex] = element;
    }, []);

    return (
        <section
            className={classnames(styles.itemGrid, styles[size], renderSlotLabel !== undefined && styles.withLabels, className)}
            aria-label={ariaLabel}
        >
            {Array.from({ length: totalSlots }, (_, index) => {
                const item = items[index];

                // A drag onto the slot: the drag selected the item, the drop moves it here.
                const handleSlotDrop = (): void => {
                    const slotElement = slotElementMapRef.current[index];
                    if (!slotElement || resolvedSelectedItemId === null || onRearrangeItems === undefined) return;
                    if (item?.id === resolvedSelectedItemId && selectedItemIndex === index) return;
                    onRearrangeItems({
                        itemId: resolvedSelectedItemId,
                        fromIndex: selectedItemIndex === -1 ? null : selectedItemIndex,
                        toIndex: index,
                        displacedItemId: item?.id ?? null,
                    }, slotElement);
                };

                // A click only selects (or clears) what the slot holds; moving is by dragging or the item menu (#267).
                const handleSlotSelect = (): void => {
                    const slotElement = slotElementMapRef.current[index];
                    if (!slotElement) return;

                    if (item?.id === resolvedSelectedItemId && selectedItemIndex === index) {
                        clearSelectedItem();
                        onDeselectSelectedItem?.(slotElement);
                        return;
                    }

                    if (item?.id) {
                        toggleSelectedItem(item.id, container);
                    } else {
                        clearSelectedItem();
                    }

                    if (item && onSelectItem !== undefined) {
                        onSelectItem(item, index, slotElement);
                        return;
                    }

                    onClick?.(item, index, slotElement);
                };

                const slot = (
                    <ItemSlot
                        size={size}
                        ariaLabel={`Slot ${index + 1}, ${item === undefined ? 'empty' : item.name}`}
                        ref={(element): void => assignSlotElement(index, element)}
                        isSelected={
                            resolvedSelectedSlotIndex === index
                            || (
                                item?.id !== undefined
                                && selectedTransferItemId === item.id
                            )
                        }
                        onPlace={onRearrangeItems === undefined ? undefined : handleSlotDrop}
                        onSelect={handleSlotSelect}
                        // Dragging selects the item; the slot it is dropped on places it, as a click would.
                        onDragStart={item === undefined || onRearrangeItems === undefined ? undefined : (): void => selectItem(item.id, container)}
                        onOpenMenu={item === undefined || openItemMenu === undefined ? undefined : (anchor): void => openItemMenu(item.id, container, anchor)}
                        rarity={item?.instance?.rarity}
                        comparison={item === undefined ? undefined : itemComparisons?.[item.id]}
                    >
                        <CarriedSlotContent item={item} pending={NO_COALESCENCES} />
                    </ItemSlot>
                );
                // The label sits beside the slot, not in it: the slot is itself a button.
                return renderSlotLabel === undefined
                    ? <Fragment key={index}>{slot}</Fragment>
                    : (
                        <div key={index} className={styles.cell}>
                            {slot}
                            {renderSlotLabel(index)}
                        </div>
                    );
            })}
        </section>
    );
}
